import { useMemo } from 'react'
import type { PID } from '@fs/zion-tree-types'
import { getTopHintCategory, type HintCategory } from '@fs/zion-research-help'
import { useFeatureFlag } from '@fs/zion-flags'
import useMyHeritageHints from '../../data-service/useMyHeritageHints'
import useShowMyHeritageHints from '../../data-service/useShowMyHeritageHints'
import { getDataProblemSuggestions, getResearchSuggestionSuggestions } from '../../data-service/researchHelpHelpers'
import type { ResearchHelpData } from '../../data-service/useResearchHelpData'
import type { HintCounts } from './hintCategories'
import { EMPTY_HINT_COUNTS, getTotalHintCount, preventsTempleWork } from './hintCategories'

export type PersonHintSummary = {
  counts: HintCounts
  /**
   * Highest-priority category the person has, or undefined when they have none — the icon renders
   * nothing. The ordering lives in `@fs/zion-research-help` so this badge and the person-banner
   * indicator agree on which hint matters most.
   */
  topCategory: HintCategory | undefined
  /**
   * The number for the hints tab. With the badge on, this is the total across the six categories,
   * which is what the badge reflects. With it off, it is the research-help count the tab showed
   * before this feature, so the tab reads exactly as it used to. Neither includes tree-to-tree
   * matches, which have no icon category — the caller adds those.
   */
  total: number
}

type Props = {
  personId: PID | undefined
  researchHelpData: ResearchHelpData
}

/**
 * Per-category hint counts for the focus person, plus the category the hint icon should show.
 *
 * Gated by treeWeb_personR9_tabHintIcons. With the flag off nothing extra is fetched and the summary
 * reports the pre-feature count, so the tab is byte-for-byte what it was.
 *
 * Everything here is already fetched elsewhere on the person page — research help comes in through
 * ResearchHelpContext, and the MyHeritage hook is SWR-backed, so it dedupes with the research help
 * card rather than issuing an extra request.
 */
export default function usePersonHintSummary({ personId, researchHelpData }: Props): PersonHintSummary {
  const showHintIcon = useFeatureFlag('treeWeb_personR9_tabHintIcons').isOn
  // MyHeritage carries its own gate — the partner flag plus a living/visibility check on the person
  // — and the badge must honour it, or it would both count hints the page is not allowed to show
  // and send people to MyHeritage who were never eligible.
  const showMyHeritageHints = useShowMyHeritageHints()
  // Dark clients make no MyHeritage request: nothing else on the source-centric page reads
  // these hints, so with the badge off the fetch would be for a number nobody renders.
  const { totalHints: myHeritageCount } = useMyHeritageHints({
    personId: showHintIcon && showMyHeritageHints ? personId : undefined,
  })

  return useMemo(() => {
    if (!showHintIcon) {
      return { counts: EMPTY_HINT_COUNTS, topCategory: undefined, total: researchHelpData.count }
    }

    const duplicateCount = researchHelpData.duplicatePersonDataProblems?.length ?? 0
    // getDataProblemSuggestions folds the possible duplicates in with the rest; they rank on their
    // own, so drop them back out before splitting the remainder by temple impact.
    const dataProblems = getDataProblemSuggestions(researchHelpData).filter(
      (problem) => problem.type?.name !== 'POSSIBLE_DUPLICATE_PERSON'
    )
    const templeBlocking = dataProblems.filter(preventsTempleWork)

    const counts: HintCounts = {
      ...EMPTY_HINT_COUNTS,
      'possible-duplicates': duplicateCount + templeBlocking.length,
      // Reference Tree (ATB) hints are deliberately left out of the badge for now. The category
      // keeps its place in @fs/zion-research-help's priority order, so surfacing it later is a
      // matter of counting useAtbHints' addable people here — see FamiliesStack for how that hook
      // is gated on treeWeb_personR9_referenceTreeHints.
      'data-problems': dataProblems.length - templeBlocking.length,
      'record-hints': researchHelpData.recordHints?.length ?? 0,
      'myheritage-matches': myHeritageCount,
      'research-suggestions': getResearchSuggestionSuggestions(researchHelpData).length,
    }

    return { counts, topCategory: getTopHintCategory(counts), total: getTotalHintCount(counts) }
  }, [myHeritageCount, researchHelpData, showHintIcon])
}
